import { useState } from "react";
import { Link } from "react-router-dom";
import { PersonArmsSpread, X, TextAa, Eye, HandWaving, SpeakerSlash } from "@phosphor-icons/react";
import { useAccess } from "@/context/AccessContext";

const SIZES = [["normal", "Normal"], ["large", "Large"], ["xlarge", "Largest"]];

/** Floating accessibility controls, on every page, keyboard reachable. */
export const AccessButton = () => {
  const { prefs, setPref, announce } = useAccess();
  const [open, setOpen] = useState(false);

  const Toggle = ({ label, hint, icon: Icon, on, onChange, testid }) => (
    <button type="button" data-testid={testid} aria-pressed={on} onClick={() => onChange(!on)}
      className={`flex w-full items-start gap-3 border p-3 text-left transition-colors duration-150 ${
        on ? "border-[#002FA7] bg-[#EEF3FF]" : "border-border bg-white hover:border-[#002FA7]"}`}>
      <Icon size={20} className={on ? "text-[#002FA7]" : "text-slate-500"} />
      <span className="min-w-0 flex-1">
        <span className="block text-sm font-semibold text-[#0F172A]">{label}</span>
        <span className="block text-xs text-slate-600">{hint}</span>
      </span>
      <span className="text-xs font-bold uppercase text-slate-500">{on ? "On" : "Off"}</span>
    </button>
  );

  return (
    <>
      <button type="button" data-testid="access-button" aria-expanded={open}
        aria-label="Accessibility settings"
        onClick={() => setOpen((v) => !v)}
        className="fixed bottom-20 right-4 z-[75] flex h-12 w-12 items-center justify-center border-2 border-white bg-[#002FA7] text-white shadow-lg lg:bottom-6">
        {open ? <X size={22} /> : <PersonArmsSpread size={22} weight="bold" />}
      </button>

      {open && (
        <div role="dialog" aria-label="Accessibility settings" data-testid="access-panel"
          className="fixed bottom-36 right-4 z-[75] w-[min(22rem,calc(100vw-2rem))] border border-border bg-white p-4 shadow-2xl lg:bottom-20">
          <p className="overline text-[#002FA7]">Accessibility</p>
          <p className="mt-1 text-sm text-slate-600">
            These settings stay on this device.
          </p>

          <p className="mt-4 text-sm font-semibold text-[#0F172A]">
            <TextAa size={16} className="mr-1.5 inline align-[-3px] text-[#002FA7]" />Text size
          </p>
          <div className="mt-2 flex gap-2">
            {SIZES.map(([value, label]) => (
              <button key={value} type="button" data-testid={`access-text-${value}`}
                aria-pressed={prefs.textSize === value}
                onClick={() => { setPref("textSize", value); announce(`Text size ${label}`); }}
                className={`tap-safe flex-1 border px-2 py-2 text-xs font-semibold transition-colors duration-150 ${
                  prefs.textSize === value ? "border-[#0F172A] bg-[#0F172A] text-white"
                    : "border-border bg-white text-slate-700 hover:border-[#0F172A]"}`}>
                {label}
              </button>
            ))}
          </div>

          <div className="mt-3 space-y-2">
            <Toggle testid="access-contrast" label="High contrast" icon={Eye}
              hint="Stronger colours and heavier borders" on={prefs.contrast}
              onChange={(v) => { setPref("contrast", v); announce(v ? "High contrast on" : "High contrast off"); }} />
            <Toggle testid="access-motion" label="Reduce motion" icon={HandWaving}
              hint="Stops animations and transitions" on={prefs.reduceMotion}
              onChange={(v) => { setPref("reduceMotion", v); announce(v ? "Motion reduced" : "Motion restored"); }} />
            <Toggle testid="access-visual-alerts" label="Show alerts on screen" icon={SpeakerSlash}
              hint="Flashes a banner instead of relying on a sound" on={prefs.visualAlerts}
              onChange={(v) => { setPref("visualAlerts", v); announce(v ? "Visual alerts on" : "Visual alerts off"); }} />
          </div>

          <Link to="/accessibility" data-testid="access-more" onClick={() => setOpen(false)}
            className="tap-safe mt-3 inline-block text-sm font-semibold text-[#002FA7] hover:underline">
            What else is built in
          </Link>
        </div>
      )}
    </>
  );
};
